import { makeAutoObservable } from "mobx";
import { FormSubmitMethod } from "helpers/decorators/formSubmitMethod";
import { directorFormToDirectorCreateData, directorFormToDirectorUpdateData } from "helpers/forms/director";
import type { DirectorStepFormValues } from "helpers/forms/directorTypes";
import type { CompanyStore } from "services/company/companyStore";
import { DirectorAddressKey, DirectorAddressStore } from "services/director/directorAddressStore";
import { type DirectorStore } from "services/director/directorStore";
import { DirectorPositionStore } from "services/directorPosition/directorPositionStore";
import { FormError } from "types/errors";
import { type FormSubmitResult } from "types/form";

export enum DirectorFormStep {
  Position = "Position",
  NameData = "NameData",
  DateOfBirth = "DateOfBirth",
  Nationality = "Nationality",
  Address = "Address",
  PreviousAddress = "PreviousAddress",
  Contacts = "Contacts",
}

const BASE_STEPS = [
  DirectorFormStep.Position,
  DirectorFormStep.NameData,
  DirectorFormStep.DateOfBirth,
  DirectorFormStep.Nationality,
  DirectorFormStep.Address,
  DirectorFormStep.Contacts,
];

export class DirectorFormStore {
  private _activeStep: DirectorFormStep = DirectorFormStep.Position;
  private readonly _editMode: boolean;
  private _activeDirector: DirectorStepFormValues | null = null;
  private _isCurrentAddressLessThanOneYear = false;

  constructor(
    private readonly _directorStore: DirectorStore,
    private readonly _companyStore: CompanyStore,
    private readonly _addressStore: DirectorAddressStore,
    private readonly _directorPositionsStore: DirectorPositionStore,
    private readonly _isResidenceDurationEnabled: boolean,
    private _directorId?: string,
  ) {
    this._editMode = !!_directorId;
    makeAutoObservable(this);
  }

  get activeStep(): DirectorFormStep {
    return this._activeStep;
  }

  set activeStep(value: DirectorFormStep) {
    this._activeStep = value;
  }

  get steps(): DirectorFormStep[] {
    if (!this._isResidenceDurationEnabled || !this._isCurrentAddressLessThanOneYear) {
      return BASE_STEPS;
    }

    const steps = [...BASE_STEPS];
    steps.splice(steps.indexOf(DirectorFormStep.Address) + 1, 0, DirectorFormStep.PreviousAddress);
    return steps;
  }

  get isFirstStep(): boolean {
    return this.activeStep === this.steps[0];
  }

  get isLastStep(): boolean {
    return this.activeStep === this.steps.at(-1);
  }

  get editMode(): boolean {
    return this._editMode;
  }

  get activeDirector(): DirectorStepFormValues | null {
    return this._activeDirector;
  }

  set activeDirector(value: DirectorStepFormValues | null) {
    this._activeDirector = value;
  }

  getDirectorId(): string {
    if (!this._directorId) {
      throw new FormError("No director's id");
    }

    return this._directorId;
  }

  getDirectorFirstName(): string {
    if (!this._activeDirector) {
      throw new FormError("No active director found!");
    }
    return this._activeDirector.firstName || "";
  }

  isSkipSave(): boolean {
    if (
      this.activeStep === DirectorFormStep.Position ||
      this.activeStep === DirectorFormStep.NameData ||
      this.activeStep === DirectorFormStep.DateOfBirth
    ) {
      return false;
    }
    return true;
  }

  @FormSubmitMethod()
  async onSubmit(values: DirectorStepFormValues): Promise<FormSubmitResult> {
    switch (this.activeStep) {
      case DirectorFormStep.Position:
      case DirectorFormStep.DateOfBirth:
      case DirectorFormStep.NameData:
      case DirectorFormStep.Nationality:
      case DirectorFormStep.PreviousAddress:
        break;
      case DirectorFormStep.Address:
        this._isCurrentAddressLessThanOneYear = Boolean(values.isCurrentAddressLessThanOneYear);
        break;
      case DirectorFormStep.Contacts:
        if (this.isLastStep) {
          await this._updateCommonData(values);
          await this._updateAddress(values);
        }
        break;
    }

    if (!this.isLastStep) {
      this.goForward();
    }
  }

  goBack(): void {
    this.activeStep = this.steps[this.steps.indexOf(this.activeStep) - 1] as unknown as DirectorFormStep;
  }

  goForward(): void {
    this.activeStep = this.steps[this.steps.indexOf(this.activeStep) + 1] as unknown as DirectorFormStep;
  }

  private async _updateCommonData(values: DirectorStepFormValues): Promise<void> {
    if (!values.position) {
      throw new FormError("Something went wrong, no director's position in the form");
    }

    if (this._directorId) {
      await this._directorStore.updateDirector(this._directorId, directorFormToDirectorUpdateData(values));
    } else {
      this._directorId = await this._directorStore.createDirector(
        directorFormToDirectorCreateData(this._companyStore.getCompany().id, values),
      );
    }

    this._directorPositionsStore.setForDirector(this._directorId, values.position);
  }

  private async _updateAddress(values: DirectorStepFormValues): Promise<void> {
    if (!values.address) {
      throw new FormError("Something went wrong, no address in the form");
    }

    await this._addressStore.updateForDirector(this.getDirectorId(), values.address);

    if (!this._isResidenceDurationEnabled) {
      return;
    }

    if (values.isCurrentAddressLessThanOneYear && values.previousAddressInUk && values.previousAddress) {
      await this._addressStore.updateForDirector(
        this.getDirectorId(),
        values.previousAddress,
        DirectorAddressKey.Previous,
      );
    } else {
      this._addressStore.clearPreviousAddressForDirector(this.getDirectorId());
    }
  }
}
